import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  deleteStalledActivation,
  getStalledActivations,
  resendActivationEmail,
} from "./firestoreService";

const typeLabelKey = {
  FREE_USER: "stalled.typeFree",
  PERSONAL_PRO: "stalled.typePersonalPro",
  TEAM_PRO: "stalled.typeTeamPro",
};

const statusOrder = { expired: 0, stalled: 1, fresh: 2 };

const statusChipProps = (status) => {
  switch (status) {
    case "expired":
      return { color: "error", labelKey: "stalled.statusExpired" };
    case "stalled":
      return { color: "warning", labelKey: "stalled.statusStalled" };
    default:
      return { color: "default", labelKey: "stalled.statusFresh" };
  }
};

function StalledActivations() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [data, setData] = useState({
    rows: [],
    totalCount: 0,
    stalledCount: 0,
    expiredCount: 0,
    stalledThresholdDays: 3,
    expiredThresholdDays: 7,
  });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getStalledActivations();
      const sorted = [...(result.rows || [])].sort((a, b) => {
        const so = statusOrder[a.status] - statusOrder[b.status];
        if (so !== 0) return so;
        return (b.ageDays || 0) - (a.ageDays || 0);
      });
      setData({ ...result, rows: sorted });
    } catch (e) {
      setError(t("stalled.errorGeneric", { message: e.message || "Unknown error" }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const onResend = async (row) => {
    setActionBusy(true);
    setError("");
    setSuccess("");
    try {
      await resendActivationEmail(row.token);
      setSuccess(t("stalled.resendSuccess", { email: row.email }));
      await load();
    } catch (e) {
      setError(t("stalled.errorGeneric", { message: e.message || "Unknown error" }));
    } finally {
      setActionBusy(false);
    }
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    setActionBusy(true);
    setError("");
    setSuccess("");
    try {
      await deleteStalledActivation(deleteTarget.token);
      setSuccess(t("stalled.deleteSuccess"));
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setError(t("stalled.errorGeneric", { message: e.message || "Unknown error" }));
    } finally {
      setActionBusy(false);
    }
  };

  const summaryText =
    data.stalledCount === 0
      ? t("stalled.empty")
      : data.expiredCount > 0
      ? t("stalled.summaryWithExpired", {
          count: data.stalledCount,
          expired: data.expiredCount,
          ttl: data.expiredThresholdDays,
        })
      : t("stalled.summary", {
          count: data.stalledCount,
          days: data.stalledThresholdDays,
        });

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h4" component="h1">
          {t("stalled.title")}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={load}
          disabled={loading || actionBusy}
        >
          {t("stalled.refresh")}
        </Button>
      </Stack>

      <Typography variant="body1" sx={{ mb: 2 }} color="text.secondary">
        {summaryText}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : data.rows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">{t("stalled.empty")}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("stalled.columnEmail")}</TableCell>
                <TableCell>{t("stalled.columnDisplayName")}</TableCell>
                <TableCell>{t("stalled.columnType")}</TableCell>
                <TableCell>{t("stalled.columnOrgName")}</TableCell>
                <TableCell>{t("stalled.columnCreated")}</TableCell>
                <TableCell>{t("stalled.columnAge")}</TableCell>
                <TableCell>{t("stalled.columnStatus")}</TableCell>
                <TableCell align="right">
                  {t("stalled.columnActions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map((row) => {
                const chip = statusChipProps(row.status);
                const typeKey = typeLabelKey[row.signUpOption];
                return (
                  <TableRow key={row.token} hover>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.displayName || "—"}</TableCell>
                    <TableCell>{typeKey ? t(typeKey) : row.signUpOption}</TableCell>
                    <TableCell>{row.requestedOrgName || "—"}</TableCell>
                    <TableCell>
                      {row.createdAt
                        ? new Date(row.createdAt).toLocaleDateString()
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {t("stalled.ageDays", {
                        count: Math.floor(row.ageDays || 0),
                      })}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={chip.color}
                        label={t(chip.labelKey)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={actionBusy}
                          onClick={() => onResend(row)}
                        >
                          {t("stalled.actionResend")}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          disabled={actionBusy}
                          onClick={() => setDeleteTarget(row)}
                        >
                          {t("stalled.actionDelete")}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t("stalled.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("stalled.confirmDeleteBody", {
              email: deleteTarget?.email || "",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={actionBusy}>
            {t("stalled.confirmDeleteCancel")}
          </Button>
          <Button
            onClick={onConfirmDelete}
            color="error"
            variant="contained"
            disabled={actionBusy}
          >
            {actionBusy ? (
              <CircularProgress size={20} />
            ) : (
              t("stalled.confirmDeleteConfirm")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default StalledActivations;
